import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import CloseIcon from "@mui/icons-material/Close";
import { Alert, Box, IconButton } from "@mui/material";

/** @typedef {{ id: string, message: string, severity?: string, duration?: number, action?: React.ReactNode }} SnackbarItem */

const SnackbarContext = createContext({
  /** @param {string | { message: string, severity?: string, duration?: number, action?: React.ReactNode }} opts */
  showSnackbar: () => {},
});

function nextItemId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

export function SnackbarProvider({ children }) {
  /** @type {React.MutableRefObject<Map<string, ReturnType<typeof setTimeout>>>} */
  const timersRef = useRef(new Map());
  /** @type {[SnackbarItem[], React.Dispatch<React.SetStateAction<SnackbarItem[]>>]} */
  const [items, setItems] = useState([]);

  const removeItem = useCallback((id) => {
    const t = timersRef.current.get(id);
    if (t != null) {
      clearTimeout(t);
      timersRef.current.delete(id);
    }
    setItems((prev) => prev.filter((x) => x.id !== id));
  }, []);

  useEffect(() => {
    return () => {
      timersRef.current.forEach((t) => clearTimeout(t));
      timersRef.current.clear();
    };
  }, []);

  const showSnackbar = useCallback(
    (opts) => {
      const isStr = typeof opts === "string";
      const msg = isStr ? opts : opts?.message;
      if (msg == null || String(msg).trim() === "") return;

      const id = nextItemId();
      const rawSev = isStr ? "info" : (opts?.severity ?? "info");
      const severity =
        rawSev === "error" ||
        rawSev === "warning" ||
        rawSev === "info" ||
        rawSev === "success"
          ? rawSev
          : "info";
      const rawD = isStr ? 6000 : (opts?.duration ?? 6000);
      const duration =
        typeof rawD === "number" && rawD > 0 ? rawD : 6000;
      const action = isStr ? null : opts?.action ?? null;

      const item = {
        id,
        message: String(msg),
        severity,
        duration,
        action,
      };

      setItems((prev) => [...prev, item]);

      const tid = window.setTimeout(() => removeItem(id), duration);
      timersRef.current.set(id, tid);
    },
    [removeItem],
  );

  const value = useMemo(() => ({ showSnackbar }), [showSnackbar]);

  return (
    <SnackbarContext.Provider value={value}>
      {children}
      <Box
        sx={{
          position: "fixed",
          top: 72,
          right: 16,
          /** Above `TopBar` AppBar (`zIndex.drawer + 2`) so trial and system alerts are visible. */
          zIndex: (theme) => Math.max(theme.zIndex.snackbar, theme.zIndex.drawer + 10),
          display: "flex",
          flexDirection: "column",
          gap: 1,
          alignItems: "flex-end",
          maxWidth: "min(440px, calc(100vw - 32px))",
          width: "100%",
          pointerEvents: "none",
          "& .MuiAlert-root": {
            pointerEvents: "auto",
            width: "100%",
          },
        }}
      >
        {items.map((item) => (
          <Alert
            key={item.id}
            severity={item.severity}
            variant="filled"
            elevation={6}
            action={
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                {item.action}
                <IconButton
                  size="small"
                  aria-label="Dismiss notification"
                  color="inherit"
                  onClick={() => removeItem(item.id)}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            }
            sx={{ width: "100%", alignItems: "center" }}
          >
            {item.message}
          </Alert>
        ))}
      </Box>
    </SnackbarContext.Provider>
  );
}

export function useSnackbar() {
  return useContext(SnackbarContext);
}
